let disclosureIndex = 0;

class BabaFuchsAiDisclosure extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.ai-disclosure__trigger');
    this.panel = this.querySelector('.ai-disclosure__panel');

    if (!this.trigger || !this.panel || this.dataset.initialized === 'true') {
      return;
    }

    this.dataset.initialized = 'true';
    this.isHovered = false;
    this.isFocused = false;
    this.isPinned = false;
    this.suppressFocusOpen = false;

    disclosureIndex += 1;
    this.panel.id ||= `babafuchs-ai-disclosure-${disclosureIndex}`;
    this.trigger.setAttribute('aria-controls', this.panel.id);
    const descriptions = new Set((this.trigger.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    descriptions.add(this.panel.id);
    this.trigger.setAttribute('aria-describedby', [...descriptions].join(' '));

    this.addEventListener('pointerenter', this.handlePointerEnter);
    this.addEventListener('pointerleave', this.handlePointerLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    this.trigger.addEventListener('click', this.handleTriggerClick);
    this.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('pointerdown', this.handleDocumentPointerDown);
    window.addEventListener('resize', this.handleViewportChange, { passive: true });
    window.addEventListener('scroll', this.handleViewportChange, { passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.handlePointerEnter);
    this.removeEventListener('pointerleave', this.handlePointerLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.trigger?.removeEventListener('click', this.handleTriggerClick);
    this.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
    window.removeEventListener('resize', this.handleViewportChange);
    window.removeEventListener('scroll', this.handleViewportChange);
  }

  handlePointerEnter = (event) => {
    if (event.pointerType === 'touch') {
      return;
    }

    this.suppressFocusOpen = false;
    this.isHovered = true;
    this.syncOpenState();
  };

  handlePointerLeave = (event) => {
    if (event.pointerType === 'touch') {
      return;
    }

    this.isHovered = false;
    this.syncOpenState();
  };

  handleFocusIn = () => {
    if (this.suppressFocusOpen) {
      return;
    }

    this.isFocused = true;
    this.syncOpenState();
  };

  handleFocusOut = () => {
    requestAnimationFrame(() => {
      const focusRemainsInside = this.contains(document.activeElement);

      this.isFocused = focusRemainsInside && !this.suppressFocusOpen;

      if (!focusRemainsInside) {
        this.suppressFocusOpen = false;
      }

      this.syncOpenState();
    });
  };

  handleTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.suppressFocusOpen = false;
    this.isFocused = true;
    this.isPinned = !this.isPinned;
    this.syncOpenState();
  };

  handleKeyDown = (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    this.isPinned = false;
    this.isHovered = false;
    this.isFocused = false;
    this.suppressFocusOpen = true;
    this.syncOpenState();
    this.trigger.focus({ preventScroll: true });
  };

  handleDocumentPointerDown = (event) => {
    if (!this.contains(event.target) && this.isPinned) {
      this.isPinned = false;
      this.syncOpenState();
    }
  };

  handleViewportChange = () => {
    if (this.dataset.open === 'true') {
      this.positionPanel();
    }
  };

  syncOpenState() {
    const shouldOpen = this.isHovered || this.isFocused || this.isPinned;

    this.dataset.open = String(shouldOpen);
    this.trigger.setAttribute('aria-expanded', String(shouldOpen));
    this.panel.hidden = !shouldOpen;

    if (shouldOpen) {
      requestAnimationFrame(() => this.positionPanel());
    }
  }

  positionPanel() {
    this.dataset.panelPlacement = 'below';
    this.style.setProperty('--ai-disclosure-panel-shift-x', '0px');

    const viewportMargin = 12;
    const panelRect = this.panel.getBoundingClientRect();
    const triggerRect = this.trigger.getBoundingClientRect();
    let horizontalShift = 0;

    if (panelRect.left < viewportMargin) {
      horizontalShift += viewportMargin - panelRect.left;
    }

    if (panelRect.right + horizontalShift > window.innerWidth - viewportMargin) {
      horizontalShift -= panelRect.right + horizontalShift - (window.innerWidth - viewportMargin);
    }

    this.style.setProperty('--ai-disclosure-panel-shift-x', `${horizontalShift}px`);

    if (
      panelRect.bottom > window.innerHeight - viewportMargin &&
      triggerRect.top > panelRect.height + viewportMargin
    ) {
      this.dataset.panelPlacement = 'above';
    }
  }
}

if (!customElements.get('babafuchs-ai-disclosure')) {
  customElements.define('babafuchs-ai-disclosure', BabaFuchsAiDisclosure);
}
